import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../../services/api';

interface BudgetGoal {
  id: string;
  name: string;
  target_amount: number;
  current_amount: number;
  currency: string;
  category: string;
  deadline: string;
  color: string;
  is_completed: boolean;
}

interface Transaction {
  id: string;
  amount: number;
  transaction_type: string;
  description: string;
  transaction_date: string;
}

const BudgetGoals: React.FC = () => {
  const [goals, setGoals] = useState<BudgetGoal[]>([]);
  const [selectedGoal, setSelectedGoal] = useState<BudgetGoal | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState({ active_goals: 0, completed_goals: 0, total_target: 0, total_saved: 0 });
  const [loading, setLoading] = useState(true);
  const [showAddGoalModal, setShowAddGoalModal] = useState(false);
  const [showEditGoalModal, setShowEditGoalModal] = useState(false);
  const [showAddTransactionModal, setShowAddTransactionModal] = useState(false);
  const [newGoal, setNewGoal] = useState({ name: '', targetAmount: 0, currency: 'INR', category: '', deadline: '', color: '#22c55e' });
  const [editGoal, setEditGoal] = useState({ id: '', name: '', targetAmount: 0, currency: 'INR', category: '', deadline: '', color: '#22c55e' });
  const [newTransaction, setNewTransaction] = useState({ amount: 0, transactionType: 'deposit', description: '' });

  const fetchGoals = useCallback(async () => {
    try {
      setLoading(true);
      const [goalsRes, summaryRes] = await Promise.all([
        apiService.getBudgetGoals(),
        apiService.getBudgetSummary()
      ]);
      if (goalsRes.success) setGoals(goalsRes.goals || []);
      if (summaryRes.success) setSummary(summaryRes.summary || {});
    } catch (error) {
      console.error('Failed to fetch budget goals:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchGoalDetails = useCallback(async () => {
    if (!selectedGoal) return;
    try {
      const response = await apiService.getBudgetGoal(selectedGoal.id);
      if (response.success) {
        setTransactions(response.transactions || []);
      }
    } catch (error) {
      console.error('Failed to fetch goal details:', error);
    }
  }, [selectedGoal]);

  useEffect(() => { fetchGoals(); }, [fetchGoals]);
  useEffect(() => { fetchGoalDetails(); }, [selectedGoal, fetchGoalDetails]);

  const handleAddGoal = async () => {
    if (!newGoal.name.trim() || newGoal.targetAmount <= 0) return;
    try {
      await apiService.createBudgetGoal(newGoal);
      setNewGoal({ name: '', targetAmount: 0, currency: 'INR', category: '', deadline: '', color: '#22c55e' });
      setShowAddGoalModal(false);
      fetchGoals();
    } catch (error) {
      console.error('Failed to add goal:', error);
    }
  };

  const handleEditGoal = async () => {
    if (!editGoal.name.trim() || editGoal.targetAmount <= 0) return;
    try {
      await apiService.updateBudgetGoal(editGoal.id, {
        name: editGoal.name,
        targetAmount: editGoal.targetAmount,
        currency: editGoal.currency,
        category: editGoal.category,
        deadline: editGoal.deadline,
        color: editGoal.color
      });
      setShowEditGoalModal(false);
      fetchGoals();
    } catch (error) {
      console.error('Failed to update goal:', error);
      alert('Failed to update goal');
    }
  };

  const handleDeleteGoal = async (goalId: string) => {
    if (!window.confirm('Are you sure you want to delete this goal? All transactions will also be deleted.')) return;
    try {
      await apiService.deleteBudgetGoal(goalId);
      if (selectedGoal?.id === goalId) {
        setSelectedGoal(null);
        setTransactions([]);
      }
      fetchGoals();
    } catch (error) {
      console.error('Failed to delete goal:', error);
      alert('Failed to delete goal');
    }
  };

  const openEditModal = (goal: BudgetGoal) => {
    setEditGoal({
      id: goal.id,
      name: goal.name,
      targetAmount: goal.target_amount,
      currency: goal.currency || 'INR',
      category: goal.category || '',
      deadline: goal.deadline || '',
      color: goal.color || '#22c55e'
    });
    setShowEditGoalModal(true);
  };

  const handleAddTransaction = async () => {
    if (!selectedGoal || newTransaction.amount <= 0) return;
    try {
      await apiService.addBudgetTransaction(selectedGoal.id, newTransaction);
      setNewTransaction({ amount: 0, transactionType: 'deposit', description: '' });
      setShowAddTransactionModal(false);
      fetchGoals();
      fetchGoalDetails();
    } catch (error) {
      console.error('Failed to add transaction:', error);
    }
  };

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency }).format(amount);
  };

  const getProgressPercent = (goal: BudgetGoal) => {
    return Math.min(Math.round((goal.current_amount / goal.target_amount) * 100), 100);
  };

  const categories = ['savings', 'vacation', 'emergency', 'education', 'home', 'car', 'wedding', 'other'];

  return (
    <div style={{ padding: '24px' }} data-testid="budget-goals">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Family Budget Goals</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Track savings and financial targets</p>
        </div>
        <button
          onClick={() => setShowAddGoalModal(true)}
          data-testid="add-goal-btn"
          style={{ padding: '10px 20px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
        >
          + Add Goal
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
        <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#15803d' }}>Total Saved</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#14532d' }}>{formatCurrency(summary.total_saved)}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#eff6ff', borderRadius: '12px', border: '1px solid #bfdbfe' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#1d4ed8' }}>Target Total</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#1e3a8a' }}>{formatCurrency(summary.total_target)}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fefce8', borderRadius: '12px', border: '1px solid #fef08a' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#a16207' }}>Active Goals</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#713f12' }}>{summary.active_goals}</p>
        </div>
        <div style={{ padding: '20px', backgroundColor: '#fdf4ff', borderRadius: '12px', border: '1px solid #f5d0fe' }}>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#a21caf' }}>Completed</p>
          <p style={{ margin: '8px 0 0 0', fontSize: '1.5rem', fontWeight: 700, color: '#701a75' }}>{summary.completed_goals}</p>
        </div>
      </div>

      {/* Goals List */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : goals.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>No budget goals yet. Start saving for something special!</p>
          <button
            onClick={() => setShowAddGoalModal(true)}
            style={{ padding: '12px 24px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 600, cursor: 'pointer' }}
          >
            Create Your First Goal
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {goals.map(goal => (
            <div
              key={goal.id}
              onClick={() => setSelectedGoal(goal)}
              style={{
                padding: '20px',
                backgroundColor: selectedGoal?.id === goal.id ? '#f0fdf4' : 'white',
                borderRadius: '12px',
                border: selectedGoal?.id === goal.id ? `2px solid ${goal.color}` : '1px solid #e5e7eb',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#1f2937' }}>{goal.name}</h3>
                  {goal.category && <span style={{ fontSize: '0.75rem', color: '#6b7280', textTransform: 'capitalize' }}>{goal.category}</span>}
                </div>
                <span style={{
                  padding: '4px 12px',
                  backgroundColor: goal.is_completed ? '#dcfce7' : `${goal.color}20`,
                  color: goal.is_completed ? '#15803d' : goal.color,
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: 600
                }}>
                  {goal.is_completed ? 'Complete!' : `${getProgressPercent(goal)}%`}
                </span>
              </div>
              
              {/* Progress Bar */}
              <div style={{ backgroundColor: '#e5e7eb', borderRadius: '999px', height: '8px', overflow: 'hidden', marginBottom: '12px' }}>
                <div style={{
                  width: `${getProgressPercent(goal)}%`,
                  height: '100%',
                  backgroundColor: goal.color,
                  borderRadius: '999px',
                  transition: 'width 0.3s'
                }} />
              </div>
              
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ color: '#6b7280' }}>
                  <strong style={{ color: goal.color }}>{formatCurrency(goal.current_amount, goal.currency)}</strong> saved
                </span>
                <span style={{ color: '#6b7280' }}>
                  of {formatCurrency(goal.target_amount, goal.currency)}
                </span>
              </div>
              
              {goal.deadline && (
                <p style={{ margin: '8px 0 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>
                  Target: {new Date(goal.deadline).toLocaleDateString()}
                </p>
              )}
              
              {/* Edit/Delete Buttons */}
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px', justifyContent: 'flex-end' }}>
                <button
                  onClick={(e) => { e.stopPropagation(); openEditModal(goal); }}
                  style={{ padding: '6px 12px', backgroundColor: '#eff6ff', color: '#2563eb', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}
                >
                  Edit
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); handleDeleteGoal(goal.id); }}
                  style={{ padding: '6px 12px', backgroundColor: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 500, cursor: 'pointer' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Selected Goal Transactions */}
      {selectedGoal && (
        <div style={{ marginTop: '24px', padding: '20px', backgroundColor: '#f9fafb', borderRadius: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <h4 style={{ margin: 0, color: '#374151' }}>Transaction History</h4>
            <button
              onClick={() => setShowAddTransactionModal(true)}
              style={{ padding: '8px 16px', backgroundColor: '#22c55e', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 500, cursor: 'pointer', fontSize: '0.85rem' }}
            >
              + Add Funds
            </button>
          </div>
          {transactions.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280' }}>No transactions yet</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {transactions.map(t => (
                <div key={t.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '12px', backgroundColor: 'white', borderRadius: '8px' }}>
                  <div>
                    <p style={{ margin: 0, fontWeight: 500, color: '#1f2937' }}>{t.description || (t.transaction_type === 'deposit' ? 'Deposit' : 'Withdrawal')}</p>
                    <p style={{ margin: '2px 0 0 0', fontSize: '0.8rem', color: '#9ca3af' }}>{new Date(t.transaction_date).toLocaleDateString()}</p>
                  </div>
                  <span style={{ fontWeight: 600, color: t.transaction_type === 'deposit' ? '#22c55e' : '#ef4444' }}>
                    {t.transaction_type === 'deposit' ? '+' : '-'}{formatCurrency(t.amount, selectedGoal.currency)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Goal Modal */}
      {showAddGoalModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '450px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Create Budget Goal</h3>
            <input
              type="text"
              value={newGoal.name}
              onChange={(e) => setNewGoal({ ...newGoal, name: e.target.value })}
              placeholder="Goal name (e.g., Family Vacation)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <input
              type="number"
              value={newGoal.targetAmount || ''}
              onChange={(e) => setNewGoal({ ...newGoal, targetAmount: parseFloat(e.target.value) || 0 })}
              placeholder="Target amount"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <select
              value={newGoal.category}
              onChange={(e) => setNewGoal({ ...newGoal, category: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px' }}
            >
              <option value="">Select category</option>
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <input
              type="date"
              value={newGoal.deadline}
              onChange={(e) => setNewGoal({ ...newGoal, deadline: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#6b7280' }}>Color</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444'].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setNewGoal({ ...newGoal, color })}
                    style={{ width: '36px', height: '36px', backgroundColor: color, border: newGoal.color === color ? '3px solid #1f2937' : 'none', borderRadius: '50%', cursor: 'pointer' }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddGoalModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddGoal} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#22c55e', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Transaction Modal */}
      {showAddTransactionModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Add to &quot;{selectedGoal?.name}&quot;</h3>
            <input
              type="number"
              value={newTransaction.amount || ''}
              onChange={(e) => setNewTransaction({ ...newTransaction, amount: parseFloat(e.target.value) || 0 })}
              placeholder="Amount"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <select
              value={newTransaction.transactionType}
              onChange={(e) => setNewTransaction({ ...newTransaction, transactionType: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px' }}
            >
              <option value="deposit">Deposit</option>
              <option value="withdrawal">Withdrawal</option>
            </select>
            <input
              type="text"
              value={newTransaction.description}
              onChange={(e) => setNewTransaction({ ...newTransaction, description: e.target.value })}
              placeholder="Description (optional)"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '16px', boxSizing: 'border-box' }}
            />
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowAddTransactionModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleAddTransaction} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#22c55e', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Add</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Goal Modal */}
      {showEditGoalModal && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: 'white', borderRadius: '16px', padding: '24px', width: '90%', maxWidth: '450px', maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 16px 0' }}>Edit Budget Goal</h3>
            <input
              type="text"
              value={editGoal.name}
              onChange={(e) => setEditGoal({ ...editGoal, name: e.target.value })}
              placeholder="Goal name"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <input
              type="number"
              value={editGoal.targetAmount || ''}
              onChange={(e) => setEditGoal({ ...editGoal, targetAmount: parseFloat(e.target.value) || 0 })}
              placeholder="Target amount"
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <select
              value={editGoal.category}
              onChange={(e) => setEditGoal({ ...editGoal, category: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px' }}
            >
              <option value="">Select category</option>
              {categories.map(c => <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>)}
            </select>
            <input
              type="date"
              value={editGoal.deadline}
              onChange={(e) => setEditGoal({ ...editGoal, deadline: e.target.value })}
              style={{ width: '100%', padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', marginBottom: '12px', boxSizing: 'border-box' }}
            />
            <div style={{ marginBottom: '16px' }}>
              <label style={{ display: 'block', marginBottom: '6px', fontSize: '0.85rem', color: '#6b7280' }}>Color</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {['#22c55e', '#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#ef4444'].map(color => (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setEditGoal({ ...editGoal, color })}
                    style={{ width: '36px', height: '36px', backgroundColor: color, border: editGoal.color === color ? '3px solid #1f2937' : 'none', borderRadius: '50%', cursor: 'pointer' }}
                  />
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button onClick={() => setShowEditGoalModal(false)} style={{ flex: 1, padding: '12px', border: '1px solid #d1d5db', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>Cancel</button>
              <button onClick={handleEditGoal} style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', backgroundColor: '#22c55e', color: 'white', cursor: 'pointer', fontWeight: 600 }}>Save Changes</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BudgetGoals;
